import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Announcement } from '../models/announcement';
import { ILogger, LOGGER_TOKEN } from './logger/ilogger';

interface AnnouncementListResponse {
  count: number;
  activeCount?: number;
  docs: Announcement[];
}

@Injectable({
  providedIn: 'root'
})
export class AnnouncementManagementService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject<ILogger>(LOGGER_TOKEN);

  getAnnouncements(skip: number, limit: number, status: 'active' | 'inactive' = 'active'): Observable<AnnouncementListResponse> {
    const params = new HttpParams()
      .set('$skip', skip)
      .set('$limit', limit)
      .set('status', status);

    return this.http.get<AnnouncementListResponse>(
      `${environment.apiUrl}notification/list`,
      { params }
    );
  }

  saveAnnouncement(scope: Partial<Announcement>, id: string | null): Observable<Announcement> {
    const url = id
      ? `${environment.apiUrl}save/notification-management/${id}`
      : `${environment.apiUrl}save/notification-management`;

    return this.http.post<Announcement>(url, {
      ...scope,
      startDate: this.toDateOnly(scope.startDate, 'startDate'),
      endDate: this.toDateOnly(scope.endDate, 'endDate'),
    });
  }

  archiveAnnouncement(id: string): Observable<Announcement> {
    return this.http.put<Announcement>(`${environment.apiUrl}archive/notification-management/${id}`, {});
  }

  getActiveAnnouncements(): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${environment.apiUrl}announcements/active`);
  }

  acknowledgeAnnouncement(id: string): Observable<unknown> {
    return this.http.post(`${environment.apiUrl}announcements/${id}/acknowledge`, {});
  }

  private toDateOnly(value: string | Date | null | undefined, fieldName: string): string | null | undefined {
    if (value === null) {
      return null;
    }
    if (value === undefined) {
      return undefined;
    }
    if (typeof value === 'string') {
      const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (calendarDate) {
        const year = Number(calendarDate[1]);
        const month = Number(calendarDate[2]);
        const day = Number(calendarDate[3]);
        const parsedCalendarDate = new Date(Date.UTC(year, month - 1, day));
        if (parsedCalendarDate.getUTCFullYear() === year &&
          parsedCalendarDate.getUTCMonth() === month - 1 &&
          parsedCalendarDate.getUTCDate() === day) {
          return value;
        }
        this.logger.warn('Unable to serialize announcement date; field omitted from save payload', { field: fieldName });
        return undefined;
      }
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      this.logger.warn('Unable to serialize announcement date; field omitted from save payload', { field: fieldName });
      return undefined;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

}
